import { Component, type ErrorInfo, type ReactNode } from 'react';

import { reporter } from './telemetry.js';

interface Props {
  children: ReactNode;
}

interface State {
  failed: boolean;
}

/**
 * The last line of defence for a render error (Phase 9): reports it, then shows a plain panel
 * with a reload button instead of a blank page. Deliberately free of the UI package so a
 * fault in it can still be shown.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    reporter.capture(error, {
      handled: false,
      tags: { mechanism: 'react' },
      extra: { componentStack: info.componentStack ?? '' },
    });
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <div
        role="alert"
        data-testid="error-boundary"
        style={{
          maxWidth: 480,
          margin: '15vh auto',
          padding: 24,
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <h1 style={{ fontSize: 20 }}>Something broke</h1>
        <p>The page hit an error it could not recover from. Reloading usually fixes it.</p>
        <button type="button" onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    );
  }
}
